import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, MessageCircle, Phone, ArrowRight } from "lucide-react";
import { BRAND, NAV_LINKS, MAIN_NAV, WA_LINK, CATEGORIES } from "../lib/constants";
import { Button } from "./ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetTrigger,
  SheetClose,
} from "./ui/sheet";

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/90 backdrop-blur-xl border-b border-black/5 shadow-sm"
          : "bg-white/70 backdrop-blur-md"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-10 h-[78px] flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="brand-logo-link">
          <img
            src={BRAND.logo}
            alt="TASSEI logo"
            className="h-12 w-auto object-contain"
          />
          <div className="hidden sm:block leading-none">
            <div className="font-heading font-extrabold text-[#0A192F] text-lg tracking-tight">
              TASSEI<span className="text-[#00B37E]">.in</span>
            </div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#64748B] mt-0.5 font-semibold">
              Sapno Se Safalta Tak
            </div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {MAIN_NAV.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              data-testid={`nav-${link.label.toLowerCase()}`}
              className={({ isActive }) =>
                `px-4 py-2 text-sm font-semibold rounded-md transition-colors ${
                  isActive ? "text-[#00B37E]" : "text-[#0A192F] hover:text-[#00B37E]"
                }`
              }
              end={link.to === "/"}
            >
              {link.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <a
            href={WA_LINK("Hi TASSEI, I want a free consultation")}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-consultation-cta"
            className="hidden md:inline-flex"
          >
            <Button className="bg-[#00B37E] hover:bg-[#009166] text-white font-semibold rounded-full px-5 h-10 shadow-sm hover:shadow-md transition-all">
              <MessageCircle className="w-4 h-4 mr-2" /> Free Consultation
            </Button>
          </a>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                aria-label="Open menu"
                data-testid="header-menu-button"
                className="inline-flex items-center gap-2 h-10 px-3.5 rounded-full bg-[#0A192F] hover:bg-[#0F2648] text-white text-sm font-semibold transition-colors"
              >
                <Menu className="w-4 h-4" />
                <span>Menu</span>
              </button>
            </SheetTrigger>

            <SheetContent side="right" className="w-full sm:max-w-md p-0 bg-white" data-testid="header-menu-drawer">
              <SheetHeader className="px-6 pt-6 pb-4 border-b border-slate-100 text-left">
                <div className="flex items-center gap-3">
                  <img src={BRAND.logo} alt="TASSEI" className="h-12 w-auto object-contain" />
                  <div>
                    <SheetTitle className="font-heading font-extrabold text-[#0A192F] text-lg">
                      TASSEI<span className="text-[#00B37E]">.in</span>
                    </SheetTitle>
                    <SheetDescription className="text-[10px] uppercase tracking-[0.18em] text-[#64748B] mt-0.5 font-semibold">
                      Sapno Se Safalta Tak
                    </SheetDescription>
                  </div>
                </div>
              </SheetHeader>

              <div className="overflow-y-auto h-[calc(100vh-96px)] px-6 py-6">
                <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-[#00B37E]">Navigate</div>
                <nav className="mt-3 flex flex-col">
                  {NAV_LINKS.map((link) => (
                    <SheetClose asChild key={link.to}>
                      <NavLink
                        to={link.to}
                        data-testid={`drawer-nav-${link.label.toLowerCase()}`}
                        className={({ isActive }) =>
                          `group flex items-center justify-between py-3 border-b border-slate-100 text-base font-semibold ${
                            isActive ? "text-[#00B37E]" : "text-[#0A192F] hover:text-[#00B37E]"
                          }`
                        }
                        end={link.to === "/"}
                      >
                        <span>{link.label}</span>
                        <ArrowRight className="w-4 h-4 opacity-40 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
                      </NavLink>
                    </SheetClose>
                  ))}
                </nav>

                <div className="mt-7 text-[11px] uppercase tracking-[0.2em] font-bold text-[#00B37E]">Browse Categories</div>
                <div className="mt-3 grid grid-cols-1 gap-1.5">
                  {CATEGORIES.map((c) => (
                    <SheetClose asChild key={c.id}>
                      <Link
                        to={`/courses?cat=${c.id}`}
                        data-testid={`drawer-cat-${c.id}`}
                        className="flex items-center justify-between px-3 py-2.5 rounded-lg hover:bg-slate-50 text-sm font-medium text-[#0A192F]"
                      >
                        <span>{c.name}</span>
                        <span className="text-[10px] uppercase tracking-wider text-[#64748B] font-bold">{c.stat}</span>
                      </Link>
                    </SheetClose>
                  ))}
                </div>

                <div className="mt-7 grid grid-cols-1 gap-2">
                  <a
                    href={WA_LINK("Hi TASSEI, I want a free consultation")}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="drawer-consultation-cta"
                  >
                    <Button className="w-full bg-[#00B37E] hover:bg-[#009166] text-white font-semibold rounded-full h-11">
                      <MessageCircle className="w-4 h-4 mr-2" /> Book Free Consultation
                    </Button>
                  </a>
                  <a href={`tel:${BRAND.phoneRaw}`} data-testid="drawer-call-cta">
                    <Button variant="outline" className="w-full border-2 border-[#0A192F] text-[#0A192F] font-semibold rounded-full h-11">
                      <Phone className="w-4 h-4 mr-2" /> Call {BRAND.phone}
                    </Button>
                  </a>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Header;
